import { useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'

import {
  fetchMe,
  selectBootAttempted,
  selectIsAuthenticated,
  selectSessionStatus,
  selectSessionUser
} from '@/features/auth/slices/sessionSlice'
import { useAppDispatch, useAppSelector } from '@/store'

/**
 * The SESSION gate (PX-W6.1) — guard order position 1 of 3 (I-5):
 * session (`AuthGuard`) → org bootstrap → role (`RoleGuarded`).
 *
 * It REDIRECTS, where `RoleGuarded` refuses in place. The asymmetry is
 * deliberate: a missing session is a STATE TO FIX, so move the visitor to the
 * one place that can fix it; a denied role is a FACT TO STATE, so show it
 * where they asked and leave the URL inspectable. Do not harmonise the two.
 *
 * Boot probe: on a COLD load `anonymous` does not yet mean "signed out" — the
 * httpOnly refresh cookie has not been tried — so `fetchMe` is dispatched and
 * null renders while it settles. The exactly-once guarantee is two-layered:
 * `fetchMe`'s own `pending` reducer flips `bootAttempted` (no module-level
 * latch), and the thunk's `condition` reads the LIVE store so a StrictMode
 * double-invoked effect cannot dispatch it twice. `bootAttempted` matters
 * ONLY on the anonymous path: a session established by `signIn` does not set
 * it (there is nothing to probe — the session is in hand), and an
 * `authenticated` store renders children regardless. Gating on
 * `!bootAttempted` unconditionally would blank every post-login navigation —
 * the C1 regression.
 *
 * Zero-org bootstrap (D4): an authenticated caller with no organisation is a
 * registrant mid-onboarding — they are redirected to `/onboarding/01` (the
 * first-org flow) BEFORE role gating, so a least-privilege `consultant`
 * derivation never has to gate anything.
 */
export function AuthGuard({ children }: { children: React.ReactNode }) {
  const dispatch = useAppDispatch()
  const { pathname } = useLocation()
  const bootAttempted = useAppSelector(selectBootAttempted)
  const status = useAppSelector(selectSessionStatus)
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const user = useAppSelector(selectSessionUser)

  useEffect(() => {
    if (!bootAttempted && status === 'anonymous') {
      void dispatch(fetchMe())
    }
  }, [bootAttempted, status, dispatch])

  if (status === 'authenticating') {
    // A probe or sign-in is in flight: render nothing rather than flash a
    // redirect to /login and back.
    return null
  }

  if (!isAuthenticated) {
    if (!bootAttempted) {
      // The cold-boot probe has not settled yet — still deciding.
      return null
    }
    return <Navigate to="/login" replace />
  }

  const { organisations = [] } = user ?? {}
  if (organisations.length === 0 && !pathname.startsWith('/onboarding')) {
    return <Navigate to="/onboarding/01" replace />
  }

  return <>{children}</>
}

export default AuthGuard
